import { useState } from 'react';

const initialAccount = {
  name: 'Calie Setchfield',
  email: 'calie@pivotandprosper.com',
  agency: 'Pivot & Prosper',
};

const initialBranding = {
  color: '#F2A8B8',
  footer: 'Generated by Pivot & Prosper · pivotandprospermarketing.com',
  logo: null,
};

const initialNotifications = {
  reportComplete: true,
  weeklySummary: true,
  rankingDrop: false,
  notifEmail: 'calie@pivotandprosper.com',
};

const integrations = [
  {
    id: 'gsc',
    name: 'Google Search Console',
    description: 'Pulls ranking data, impressions, and click-through rates for gap analysis and keyword tracking.',
    status: 'disconnected',
    detail: null,
  },
  {
    id: 'dataforseo',
    name: 'DataForSEO',
    description: 'Provides real search volume, SERP analysis, and competitor backlink data.',
    status: 'connected',
    detail: '••••••@pivotandprosper••••••',
  },
  {
    id: 'claude',
    name: 'Claude AI',
    description: 'Powers AI-driven analysis, content briefs, and report narrative generation.',
    status: 'connected',
    detail: 'Claude Sonnet 4.6',
  },
];

export default function Settings() {
  const [account, setAccount] = useState(initialAccount);
  const [accountSaved, setAccountSaved] = useState(false);
  const [branding, setBranding] = useState(initialBranding);
  const [brandingSaved, setBrandingSaved] = useState(false);
  const [notifications, setNotifications] = useState(initialNotifications);
  const [notifSaved, setNotifSaved] = useState(false);
  const [integrationStates, setIntegrationStates] = useState({
    gsc: 'disconnected', dataforseo: 'connected', claude: 'connected',
  });

  function saveAccount(e) {
    e.preventDefault();
    setAccountSaved(true);
    setTimeout(() => setAccountSaved(false), 2500);
  }

  function saveBranding(e) {
    e.preventDefault();
    setBrandingSaved(true);
    setTimeout(() => setBrandingSaved(false), 2500);
  }

  function saveNotif(e) {
    e.preventDefault();
    setNotifSaved(true);
    setTimeout(() => setNotifSaved(false), 2500);
  }

  function toggleIntegration(id) {
    setIntegrationStates(s => ({
      ...s,
      [id]: s[id] === 'connected' ? 'disconnected' : 'connected',
    }));
  }

  return (
    <div style={{ maxWidth: '700px' }}>

      {/* Header */}
      <div style={{ marginBottom: '2.25rem' }}>
        <h1 style={{
          fontFamily: 'var(--font-heading)',
          fontSize: '2.2rem',
          fontWeight: 600,
          color: 'var(--charcoal)',
          lineHeight: 1.15,
          marginBottom: '0.25rem',
        }}>
          Settings
        </h1>
        <p style={{
          fontFamily: 'var(--font-body)',
          fontSize: '0.875rem',
          color: 'var(--charcoal-muted)',
        }}>
          Manage your account, integrations, and report preferences.
        </p>
      </div>

      {/* SECTION 1 — Account */}
      <SettingsCard>
        <SectionHeader
          title="Account"
          description="Your profile and agency details."
        />
        <form onSubmit={saveAccount}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.1rem', marginBottom: '1.1rem' }}>
            <Field
              label="Full Name"
              value={account.name}
              onChange={v => setAccount(a => ({ ...a, name: v }))}
            />
            <Field
              label="Email Address"
              type="email"
              value={account.email}
              onChange={v => setAccount(a => ({ ...a, email: v }))}
            />
          </div>
          <div style={{ marginBottom: '1.4rem' }}>
            <Field
              label="Agency Name"
              value={account.agency}
              onChange={v => setAccount(a => ({ ...a, agency: v }))}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <SaveButton label="Save Changes" saved={accountSaved} />
            {accountSaved && <SavedMsg />}
          </div>
        </form>
      </SettingsCard>

      <Divider />

      {/* SECTION 2 — API Integrations */}
      <SettingsCard>
        <SectionHeader
          title="API Integrations"
          description="Connect the data sources that power your reports."
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {integrations.map(intg => {
            const status = integrationStates[intg.id];
            const connected = status === 'connected';
            return (
              <div key={intg.id} style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: '1rem',
                padding: '1.1rem 1.25rem',
                background: connected ? '#F8FCF9' : 'var(--off-white)',
                border: `1.5px solid ${connected ? '#D1FAE5' : 'var(--border)'}`,
                borderRadius: 'var(--radius-sm)',
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.6rem',
                    marginBottom: '0.2rem',
                    flexWrap: 'wrap',
                  }}>
                    <span style={{
                      fontFamily: 'var(--font-body)',
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      color: 'var(--charcoal)',
                    }}>
                      {intg.name}
                    </span>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.3rem',
                      fontFamily: 'var(--font-body)',
                      fontSize: '0.7rem',
                      fontWeight: 500,
                      letterSpacing: '0.05em',
                      padding: '0.15rem 0.6rem',
                      borderRadius: '999px',
                      background: connected ? '#ECFDF5' : '#F3F4F6',
                      color: connected ? '#065F46' : '#6B7280',
                    }}>
                      <span style={{
                        width: '6px', height: '6px',
                        borderRadius: '50%',
                        background: connected ? '#10B981' : '#9CA3AF',
                        display: 'inline-block',
                      }} />
                      {connected ? 'Connected' : 'Disconnected'}
                    </span>
                    {connected && intg.detail && (
                      <span style={{
                        fontFamily: 'var(--font-body)',
                        fontSize: '0.75rem',
                        color: 'var(--charcoal-muted)',
                        fontStyle: 'italic',
                      }}>
                        {intg.detail}
                      </span>
                    )}
                  </div>
                  <p style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.8rem',
                    color: 'var(--charcoal-muted)',
                    lineHeight: 1.5,
                  }}>
                    {intg.description}
                  </p>
                </div>
                <button
                  onClick={() => toggleIntegration(intg.id)}
                  style={{
                    fontFamily: 'var(--font-body)',
                    fontWeight: 500,
                    fontSize: '0.8rem',
                    color: connected ? '#B91C1C' : 'var(--charcoal)',
                    background: connected ? '#FDECEA' : 'var(--blush-light)',
                    border: `1.5px solid ${connected ? '#FCA5A5' : 'var(--blush-dark)'}`,
                    borderRadius: 'var(--radius-sm)',
                    padding: '0.4rem 0.9rem',
                    cursor: 'pointer',
                    flexShrink: 0,
                    transition: 'all 0.15s',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {connected ? 'Disconnect' : 'Connect'}
                </button>
              </div>
            );
          })}
        </div>
      </SettingsCard>

      <Divider />

      {/* SECTION 3 — Report Branding */}
      <SettingsCard>
        <SectionHeader
          title="Report Branding"
          description="Customise how your reports look when delivered to clients."
        />
        <form onSubmit={saveBranding}>
          {/* Logo upload */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={labelStyle}>Agency Logo</label>
            <div style={{
              border: '1.5px dashed var(--border)',
              borderRadius: 'var(--radius-sm)',
              padding: '1.25rem',
              textAlign: 'center',
              background: 'var(--off-white)',
              cursor: 'pointer',
            }}>
              <p style={{
                fontFamily: 'var(--font-body)',
                fontSize: '0.82rem',
                color: 'var(--charcoal-muted)',
                marginBottom: '0.5rem',
              }}>
                {branding.logo ? branding.logo.name : 'Drop your logo here, or click to upload'}
              </p>
              <input
                type="file"
                accept="image/*"
                onChange={e => setBranding(b => ({ ...b, logo: e.target.files[0] || null }))}
                style={{ display: 'none' }}
                id="logo-upload"
              />
              <label htmlFor="logo-upload" style={{
                fontFamily: 'var(--font-body)',
                fontSize: '0.78rem',
                fontWeight: 500,
                color: 'var(--blush-dark)',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}>
                Browse file
              </label>
            </div>
          </div>

          {/* Color + footer */}
          <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: '1.1rem', marginBottom: '1.25rem' }}>
            <div>
              <label style={labelStyle}>Brand Colour</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <input
                  type="color"
                  value={branding.color}
                  onChange={e => setBranding(b => ({ ...b, color: e.target.value }))}
                  style={{
                    width: '40px', height: '36px',
                    border: '1.5px solid var(--border)',
                    borderRadius: 'var(--radius-sm)',
                    padding: '2px',
                    cursor: 'pointer',
                    background: 'none',
                  }}
                />
                <span style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.82rem',
                  color: 'var(--charcoal-muted)',
                  letterSpacing: '0.04em',
                }}>
                  {branding.color.toUpperCase()}
                </span>
              </div>
            </div>
            <div>
              <label style={labelStyle}>Report Footer Text</label>
              <input
                type="text"
                value={branding.footer}
                onChange={e => setBranding(b => ({ ...b, footer: e.target.value }))}
                style={inputStyle}
                onFocus={e => e.target.style.borderColor = 'var(--blush-dark)'}
                onBlur={e => e.target.style.borderColor = 'var(--border)'}
              />
            </div>
          </div>

          {/* Report header preview */}
          <div style={{ marginBottom: '1.4rem' }}>
            <label style={labelStyle}>Report Header Preview</label>
            <div style={{
              border: '1.5px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              overflow: 'hidden',
            }}>
              <div style={{
                background: branding.color,
                padding: '0.9rem 1.25rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <div>
                  <div style={{
                    fontFamily: 'var(--font-heading)',
                    fontSize: '1.15rem',
                    fontWeight: 600,
                    color: 'var(--charcoal)',
                  }}>
                    SEO & GEO Intelligence Report
                  </div>
                  <div style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.72rem',
                    color: 'var(--charcoal)',
                    opacity: 0.7,
                    letterSpacing: '0.04em',
                  }}>
                    {account.agency} · {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </div>
                </div>
                <div style={{
                  width: '36px', height: '36px',
                  background: 'rgba(255,255,255,0.4)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: 'var(--font-heading)',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  color: 'var(--charcoal)',
                }}>
                  {account.agency.charAt(0)}
                </div>
              </div>
              <div style={{
                padding: '0.5rem 1.25rem',
                background: 'var(--off-white)',
                fontFamily: 'var(--font-body)',
                fontSize: '0.72rem',
                color: 'var(--charcoal-muted)',
                borderTop: '1px solid var(--border)',
              }}>
                {branding.footer}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <SaveButton label="Save Branding" saved={brandingSaved} />
            {brandingSaved && <SavedMsg />}
          </div>
        </form>
      </SettingsCard>

      <Divider />

      {/* SECTION 4 — Notifications */}
      <SettingsCard>
        <SectionHeader
          title="Notifications"
          description="Choose when and how you receive updates."
        />
        <form onSubmit={saveNotif}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem', marginBottom: '1.4rem' }}>
            <Toggle
              label="Email me when a report is complete"
              description="Receive an email as soon as a report finishes generating."
              checked={notifications.reportComplete}
              onChange={v => setNotifications(n => ({ ...n, reportComplete: v }))}
            />
            <Toggle
              label="Weekly summary email every Monday"
              description="A snapshot of all client performance from the past 7 days."
              checked={notifications.weeklySummary}
              onChange={v => setNotifications(n => ({ ...n, weeklySummary: v }))}
            />
            <Toggle
              label="Alert me when a client drops in rankings"
              description="Get notified if any tracked keyword drops 3+ positions week over week."
              checked={notifications.rankingDrop}
              onChange={v => setNotifications(n => ({ ...n, rankingDrop: v }))}
            />
          </div>
          <div style={{ marginBottom: '1.4rem' }}>
            <Field
              label="Notification Email"
              type="email"
              value={notifications.notifEmail}
              onChange={v => setNotifications(n => ({ ...n, notifEmail: v }))}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <SaveButton label="Save Notifications" saved={notifSaved} />
            {notifSaved && <SavedMsg />}
          </div>
        </form>
      </SettingsCard>

    </div>
  );
}

/* ── Sub-components ── */

function SettingsCard({ children }) {
  return (
    <div style={{
      background: 'var(--white)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-md)',
      boxShadow: 'var(--shadow-sm)',
      padding: '1.75rem 2rem',
    }}>
      {children}
    </div>
  );
}

function SectionHeader({ title, description }) {
  return (
    <div style={{ marginBottom: '1.4rem' }}>
      <h3 style={{
        fontFamily: 'var(--font-heading)',
        fontSize: '1.25rem',
        fontWeight: 600,
        color: 'var(--charcoal)',
        marginBottom: '0.2rem',
      }}>
        {title}
      </h3>
      <p style={{
        fontFamily: 'var(--font-body)',
        fontSize: '0.82rem',
        color: 'var(--charcoal-muted)',
      }}>
        {description}
      </p>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text' }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        style={inputStyle}
        onFocus={e => e.target.style.borderColor = 'var(--blush-dark)'}
        onBlur={e => e.target.style.borderColor = 'var(--border)'}
      />
    </div>
  );
}

function Toggle({ label, description, checked, onChange }) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: '1.25rem',
      padding: '0.9rem 1.1rem',
      background: checked ? 'var(--blush-light)' : 'var(--off-white)',
      border: `1.5px solid ${checked ? 'var(--blush-dark)' : 'var(--border)'}`,
      borderRadius: 'var(--radius-sm)',
      transition: 'all 0.2s ease',
    }}>
      <div>
        <p style={{
          fontFamily: 'var(--font-body)',
          fontWeight: 500,
          fontSize: '0.875rem',
          color: 'var(--charcoal)',
          marginBottom: '0.15rem',
        }}>
          {label}
        </p>
        <p style={{
          fontFamily: 'var(--font-body)',
          fontSize: '0.78rem',
          color: 'var(--charcoal-muted)',
          lineHeight: 1.45,
        }}>
          {description}
        </p>
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        style={{
          width: '44px',
          height: '24px',
          borderRadius: '999px',
          background: checked ? 'var(--blush-dark)' : '#D1D5DB',
          border: 'none',
          cursor: 'pointer',
          position: 'relative',
          flexShrink: 0,
          transition: 'background 0.2s ease',
          marginTop: '2px',
        }}
      >
        <span style={{
          position: 'absolute',
          top: '3px',
          left: checked ? '23px' : '3px',
          width: '18px',
          height: '18px',
          borderRadius: '50%',
          background: 'var(--white)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
          transition: 'left 0.2s ease',
          display: 'block',
        }} />
      </button>
    </div>
  );
}

function SaveButton({ label, saved }) {
  return (
    <button
      type="submit"
      style={{
        fontFamily: 'var(--font-body)',
        fontWeight: 500,
        fontSize: '0.875rem',
        color: 'var(--charcoal)',
        background: saved ? '#ECFDF5' : 'var(--blush)',
        border: `1.5px solid ${saved ? '#D1FAE5' : 'transparent'}`,
        borderRadius: 'var(--radius-sm)',
        padding: '0.6rem 1.3rem',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      }}
      onMouseEnter={e => { if (!saved) e.currentTarget.style.background = 'var(--blush-dark)'; }}
      onMouseLeave={e => { if (!saved) e.currentTarget.style.background = 'var(--blush)'; }}
    >
      {saved ? '✓ Saved' : label}
    </button>
  );
}

function SavedMsg() {
  return (
    <span style={{
      fontFamily: 'var(--font-body)',
      fontSize: '0.8rem',
      color: '#065F46',
    }}>
      Changes saved successfully.
    </span>
  );
}

function Divider() {
  return <div style={{ height: '1.75rem' }} />;
}

const labelStyle = {
  fontFamily: 'var(--font-body)',
  fontSize: '0.7rem',
  fontWeight: 500,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: 'var(--charcoal-muted)',
  display: 'block',
  marginBottom: '0.35rem',
};

const inputStyle = {
  fontFamily: 'var(--font-body)',
  fontSize: '0.875rem',
  color: 'var(--charcoal)',
  background: 'var(--white)',
  border: '1.5px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
  padding: '0.6rem 0.85rem',
  width: '100%',
  outline: 'none',
  transition: 'border-color 0.15s',
};
